import React, { useEffect, useRef } from "react";
import CustomButton from "components/common/Button/CustomButton";
import Search from "components/common/Search/Search";
import SelectBox from "components/common/DropDownWithIocnsForPrint copy/DropDown";
import { BsFilterCircle } from "react-icons/bs";
import { FiDelete } from "react-icons/fi";
import { classNames } from "Utils/utils";

export const REPORT_SEARCH_MIN_LENGTH = 3;
export const REPORT_SEARCH_DEBOUNCE_MS = 400;

/** Auto-fetch when search reaches min length, or clears after a previous search. */
export const useReportSearchOnType = (
  search,
  onSearch,
  { minLength = REPORT_SEARCH_MIN_LENGTH, debounceMs = REPORT_SEARCH_DEBOUNCE_MS } = {}
) => {
  const isMounted = useRef(false);
  const lastFetchedSearch = useRef(null);
  const onSearchRef = useRef(onSearch);

  useEffect(() => {
    onSearchRef.current = onSearch;
  }, [onSearch]);

  useEffect(() => {
    if (!onSearchRef.current) {
      return;
    }

    const trimmed = (search ?? "").toString().trim();

    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }

    const timer = setTimeout(() => {
      if (!onSearchRef.current) {
        return;
      }

      if (trimmed.length >= minLength) {
        if (lastFetchedSearch.current === trimmed) {
          return;
        }
        lastFetchedSearch.current = trimmed;
        onSearchRef.current();
        return;
      }

      if (lastFetchedSearch.current !== null) {
        lastFetchedSearch.current = null;
        onSearchRef.current();
      }
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [search, minLength, debounceMs]);
};

/** Standard page content wrapper for reporting screens */
export const ReportPageContent = ({ children, className }) => (
  <div
    className={classNames(
      "mx-auto my-6 max-w-full px-4 sm:px-6 lg:px-8",
      className
    )}
  >
    {children}
  </div>
);

/** Table section below filters */
export const ReportTableSection = ({ children, className }) => (
  <div
    className={classNames("mt-4 overflow-x-auto custom-scroll", className)}
  >
    {children}
  </div>
);

/** Text input filter with consistent styling */
export const ReportFilterTextField = ({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  className,
}) => (
  <ReportFilterField label={label} className={className}>
    <input
      value={value ?? ""}
      onChange={onChange}
      placeholder={placeholder}
      type={type}
      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
    />
  </ReportFilterField>
);

/** Sticky filter card wrapper used on reporting pages */
export const ReportFiltersShell = ({ children, className }) => (
  <div
    className={classNames(
      "sticky top-[67px] z-[7] rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5",
      className
    )}
  >
    {children}
  </div>
);

/** Responsive grid for filter fields */
export const ReportFilterGrid = ({ children, columns = 4, className }) => (
  <div
    className={classNames(
      "grid grid-cols-1 gap-4",
      columns >= 2 && "sm:grid-cols-2",
      columns >= 3 && "lg:grid-cols-3",
      columns >= 4 && "xl:grid-cols-4",
      className
    )}
  >
    {children}
  </div>
);

/** Consistent label + control spacing for a single filter */
export const ReportFilterField = ({
  label,
  required = false,
  children,
  className,
}) => (
  <div className={classNames("min-w-0 space-y-1.5", className)}>
    {label ? (
      <label className="block text-xs font-medium text-gray-600">
        {label}
        {required ? <span className="ml-0.5 text-red-500">*</span> : null}
      </label>
    ) : null}
    {children}
  </div>
);

export const ReportFilterSearchField = ({
  label = "Search",
  search,
  setSearch,
  onSearch,
  minSearchLength = REPORT_SEARCH_MIN_LENGTH,
  debounceMs = REPORT_SEARCH_DEBOUNCE_MS,
  placeholder = "Search",
  className,
}) => {
  useReportSearchOnType(search, onSearch, {
    minLength: minSearchLength,
    debounceMs,
  });

  return (
    <ReportFilterField label={label} className={className}>
      <Search
        search={search}
        setSearch={setSearch}
        placeholder={placeholder}
        width="w-full"
        customClassSearch="w-full border-gray-300 py-2 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
      />
    </ReportFilterField>
  );
};

export const ReportExportButton = ({
  isLoading = false,
  isUserGracePeriodOver = false,
  onExport,
  isOnlyExcel = false,
  rightAlign = false,
}) => (
  <SelectBox
    isUserGracePeriodOver={isUserGracePeriodOver}
    isLoading={isLoading}
    isOnlyExcel={isOnlyExcel}
    rightAlign={rightAlign}
    setReportType={(format) => {
      if (isUserGracePeriodOver) {
        return;
      }
      onExport?.(format);
    }}
  />
);

/** Bottom action row: Search, Clear, Export */
export const ReportFilterActions = ({
  onSearch,
  onClear,
  onExport,
  isLoading = false,
  isSearchDisabled = false,
  isClearDisabled = false,
  isUserGracePeriodOver = false,
  searchLabel = "Search",
  clearLabel = "Clear filters",
  isOnlyExcel = false,
}) => (
  <div className="mt-5 flex flex-col gap-3 border-t border-gray-100 pt-4 sm:flex-row sm:items-center sm:justify-end">
    <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3">
      <CustomButton
        Icon={<BsFilterCircle className="h-4 w-4" />}
        clickHandler={onSearch}
        label={searchLabel}
        showLoading={isLoading}
        isDisabled={isLoading || isSearchDisabled}
      />
      <CustomButton
        variant="secondary"
        isDisabled={isLoading || isClearDisabled}
        Icon={<FiDelete className="h-4 w-4" />}
        clickHandler={onClear}
        label={clearLabel}
        cssClass="whitespace-nowrap"
      />
      <ReportExportButton
        isLoading={isLoading}
        isUserGracePeriodOver={isUserGracePeriodOver}
        onExport={onExport}
        isOnlyExcel={isOnlyExcel}
      />
    </div>
  </div>
);

/** Style-only wrapper when pages need custom action handlers */
export const ReportFilterActionsBar = ({ children, className }) => (
  <div
    className={classNames(
      "mt-5 flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 pt-4 sm:gap-3",
      className
    )}
  >
    {children}
  </div>
);
